import React, { useCallback, useEffect, useMemo, useState } from "react";
import ExcelGrid from "components/ExcelGrid";
import { Pencil, Plus, Trash2 } from "lucide-react";
import SectionCard from "components/SectionCard";
import { DropdownSelect, TextInput } from "components/InputStyled";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import { logAudit } from "lib/audit";
import { supabase } from "services/supabaseClient";
import {
  normalizeMainId,
  validateFundMappingRows,
} from "./externalIdentifierUtils";

const MODULES = [
  { value: "trade_tracker", label: "TradeTracker" },
  { value: "debt_studio", label: "DebtStudio" },
  { value: "fund_planner", label: "FundPlanner" },
];
const blankRow = () => ({
  rowKey: crypto.randomUUID(),
  fp_investment_fund_id: "",
  external_id: "",
  __isEditing: true,
});

export default function ExternalIdentifiers() {
  const { profile, loading: profileLoading } = useProfile();
  const { alert, confirm } = useDialog();
  const isPlatformAdmin = !!profile?.is_admin;
  const [functions, setFunctions] = useState([]);
  const [sets, setSets] = useState([]);
  const [allMappings, setAllMappings] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [funds, setFunds] = useState([]);
  const [moduleCode, setModuleCode] = useState("");
  const [functionId, setFunctionId] = useState("");
  const [mainId, setMainId] = useState("");
  const [orgId, setOrgId] = useState("");
  const [rows, setRows] = useState([blankRow()]);
  const [loading, setLoading] = useState(true);
  const [loadingMappings, setLoadingMappings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [requestEditCell, setRequestEditCell] = useState(null);
  const [selectedSystemId, setSelectedSystemId] = useState("");
  const [showNewContext, setShowNewContext] = useState(false);
  const [newContextReady, setNewContextReady] = useState(false);
  const [editingSystemId, setEditingSystemId] = useState("");
  const [mappingEditActive, setMappingEditActive] = useState(false);
  const [draftModuleCode, setDraftModuleCode] = useState("");
  const [draftFunctionId, setDraftFunctionId] = useState("");
  const [draftMainId, setDraftMainId] = useState("");

  // ExcelGrid treats requestEditCell as an active focus instruction. Clear it
  // after one render cycle so subsequent clicks can edit other cells normally.
  useEffect(() => {
    if (!requestEditCell) return undefined;
    const timer = window.setTimeout(() => setRequestEditCell(null), 50);
    return () => window.clearTimeout(timer);
  }, [requestEditCell]);

  const loadConfiguration = useCallback(async () => {
    if (!isPlatformAdmin) return;
    setLoading(true);
    const [functionResult, setResult, orgResult, fundResult, mappingResult] = await Promise.all([
      supabase.from("external_identifier_functions").select("*").eq("status", "active").order("module_label").order("function_label"),
      supabase.from("external_identifier_sets").select("*").eq("status", "active").order("main_id"),
      supabase.from("orgs").select("id, tenant_id, name").order("name"),
      supabase.from("fp_investment_funds").select("id, org_id, tenant_id, name, fund_code, is_active").eq("is_active", true).order("name"),
      supabase.from("external_identifiers").select("id, identifier_set_id, org_id").eq("status", "active"),
    ]);
    setLoading(false);
    const error = functionResult.error || setResult.error || orgResult.error || fundResult.error || mappingResult.error;
    if (error) {
      await alert(`Unable to load External IDs: ${error.message}`, { title: "External IDs" });
      return;
    }
    setFunctions(functionResult.data || []);
    setSets(setResult.data || []);
    setOrgs(orgResult.data || []);
    setFunds(fundResult.data || []);
    setAllMappings(mappingResult.data || []);
  }, [alert, isPlatformAdmin]);

  useEffect(() => { loadConfiguration(); }, [loadConfiguration]);

  const selectedFunction = useMemo(
    () => functions.find((item) => item.id === functionId) || null,
    [functionId, functions]
  );
  const functionOptions = useMemo(
    () => functions.filter((item) => item.module_code === moduleCode),
    [functions, moduleCode]
  );
  const draftFunctionOptions = useMemo(
    () => functions.filter((item) => item.module_code === draftModuleCode),
    [draftModuleCode, functions]
  );
  const selectedSet = useMemo(
    () => sets.find((item) => item.function_id === functionId && item.normalized_main_id === normalizeMainId(mainId)) || null,
    [functionId, mainId, sets]
  );
  const eligibleFunds = useMemo(() => funds.filter((fund) => fund.org_id === orgId), [funds, orgId]);
  const fundOptions = useMemo(
    () => eligibleFunds.map((fund) => ({ value: fund.id, label: fund.fund_code ? `${fund.name} (${fund.fund_code})` : fund.name })),
    [eligibleFunds]
  );
  const supportsFundGrid = selectedFunction?.target_type === "fp_investment_fund";
  const gridIsEditable = mappingEditActive;
  const showMappingGrid =
    !!orgId &&
    supportsFundGrid &&
    !!mainId.trim() &&
    (!!selectedSystemId || (showNewContext && newContextReady));
  const organizationSystems = useMemo(() => {
    const counts = new Map();
    allMappings.filter((mapping) => mapping.org_id === orgId).forEach((mapping) => {
      counts.set(mapping.identifier_set_id, (counts.get(mapping.identifier_set_id) || 0) + 1);
    });
    return sets
      .filter((set) => counts.has(set.id))
      .map((set) => {
        const fn = functions.find((item) => item.id === set.function_id);
        return { ...set, function: fn, mappingCount: counts.get(set.id) };
      })
      .sort((a, b) => a.main_id.localeCompare(b.main_id));
  }, [allMappings, functions, orgId, sets]);
  const gridColumns = useMemo(
    () => [
      {
        key: "fp_investment_fund_id",
        label: "Fund",
        editable: gridIsEditable,
        editor: "select",
        options: fundOptions,
        width: "w-1/2",
      },
      {
        key: "external_id",
        label: "External ID",
        editable: gridIsEditable,
        mono: true,
        width: "w-1/2",
      },
      {
        key: "actions",
        label: "",
        width: "w-20",
        render: (row, currentRows) => {
          if (!gridIsEditable) return null;
          const unsavedRows = currentRows.filter((item) => !item.id);
          const isLastUnsaved = unsavedRows[unsavedRows.length - 1]?.rowKey === row.rowKey;
          return (
            <div className="flex items-center gap-2">
              {isLastUnsaved ? (
                <button type="button" className="text-green-600 hover:text-green-800"
                  title="Add row below" aria-label="Add row below"
                  onClick={() => {
                    const rowIndex = currentRows.findIndex((item) => item.rowKey === row.rowKey);
                    const nextRow = blankRow();
                    setRows((current) => {
                      const next = [...current];
                      next.splice(rowIndex + 1, 0, nextRow);
                      return next;
                    });
                    setRequestEditCell({ row: rowIndex + 1, col: 0 });
                  }}>
                  <Plus size={16} />
                </button>
              ) : null}
              <button type="button" className="text-red-500 hover:text-red-700"
                title="Delete row" aria-label="Delete row"
                onClick={() => setRows((current) => {
                  const next = current.filter((item) => item.rowKey !== row.rowKey);
                  return next.some((item) => !item.id) ? next : [...next, blankRow()];
                })}>
                <Trash2 size={16} />
              </button>
            </div>
          );
        },
      },
    ],
    [fundOptions, gridIsEditable]
  );

  useEffect(() => {
    if (!functionOptions.some((item) => item.id === functionId)) setFunctionId("");
  }, [functionId, functionOptions]);

  useEffect(() => {
    let cancelled = false;
    async function loadMappings() {
      if (!selectedSet || !orgId || !supportsFundGrid) {
        setRows([blankRow()]);
        return;
      }
      setLoadingMappings(true);
      const { data, error } = await supabase
        .from("external_identifiers")
        .select("id, fp_investment_fund_id, identifier_value")
        .eq("identifier_set_id", selectedSet.id)
        .eq("org_id", orgId)
        .eq("status", "active")
        .order("created_at");
      if (cancelled) return;
      setLoadingMappings(false);
      if (error) {
        await alert(`Unable to load mappings: ${error.message}`, { title: "External IDs" });
        return;
      }
      setRows([
        ...(data || []).map((row) => ({
          ...row,
          rowKey: row.id,
          external_id: row.identifier_value,
          __isEditing: false,
        })),
        blankRow(),
      ]);
    }
    loadMappings();
    return () => { cancelled = true; };
  }, [alert, orgId, selectedSet, supportsFundGrid]);

  const selectOrganization = (value) => {
    setOrgId(value);
    setModuleCode("");
    setFunctionId("");
    setMainId("");
    setRows([blankRow()]);
    setSelectedSystemId("");
    setShowNewContext(false);
    setNewContextReady(false);
    setEditingSystemId("");
    setMappingEditActive(false);
  };

  const openExternalSystem = (system) => {
    setModuleCode(system.function?.module_code || "");
    setFunctionId(system.function_id);
    setMainId(system.main_id);
    setSelectedSystemId(system.id);
    setShowNewContext(false);
    setNewContextReady(false);
    setEditingSystemId("");
    setMappingEditActive(false);
  };

  const startNewExternalSystem = () => {
    setModuleCode("");
    setFunctionId("");
    setMainId("");
    setRows([blankRow()]);
    setSelectedSystemId("");
    setShowNewContext(true);
    setNewContextReady(false);
    setEditingSystemId("");
    setMappingEditActive(false);
    setDraftModuleCode("");
    setDraftFunctionId("");
    setDraftMainId("");
  };

  const editExternalSystem = (system) => {
    openExternalSystem(system);
    setDraftModuleCode(system.function?.module_code || "");
    setDraftFunctionId(system.function_id);
    setDraftMainId(system.main_id);
    setEditingSystemId(system.id);
    setMappingEditActive(true);
  };

  const createExternalSystemContext = async () => {
    const draftFunction = functions.find((item) => item.id === draftFunctionId);
    if (!draftFunction || !draftMainId.trim()) {
      await alert("Select a module and function and enter an External System / Main ID.", {
        title: "External IDs",
      });
      return;
    }
    const existingSet = sets.find((item) => item.function_id === draftFunctionId
      && item.normalized_main_id === normalizeMainId(draftMainId));
    if (existingSet) {
      setModuleCode(draftModuleCode);
      setFunctionId(draftFunctionId);
      setMainId(existingSet.main_id);
      setSelectedSystemId(existingSet.id);
      setShowNewContext(false);
      setEditingSystemId("");
      setMappingEditActive(true);
    } else {
      setModuleCode(draftModuleCode);
      setFunctionId(draftFunctionId);
      setMainId(draftMainId.trim());
      setNewContextReady(true);
      setMappingEditActive(true);
    }
  };

  const saveExternalSystem = async () => {
    const draftFunction = functions.find((item) => item.id === draftFunctionId);
    if (!draftFunction || !draftMainId.trim()) {
      await alert("Select a module and function and enter an External System / Main ID.", { title: "External IDs" });
      return;
    }
    setSaving(true);
    const { data, error } = await supabase.from("external_identifier_sets").update({
      function_id: draftFunctionId,
      main_id: draftMainId.trim(),
      normalized_main_id: normalizeMainId(draftMainId),
    }).eq("id", editingSystemId).select("*").single();
    setSaving(false);
    if (error) {
      await alert(`Unable to update External System: ${error.message}`, { title: "External IDs" });
      return;
    }
    await logAudit({ category: "integration", action: "external_identifier_system_updated", success: true,
      detail: { identifier_set_id: editingSystemId, function_id: draftFunctionId, main_id: data.main_id } });
    setModuleCode(draftModuleCode);
    setFunctionId(draftFunctionId);
    setMainId(data.main_id);
    setEditingSystemId("");
    setSelectedSystemId(data.id);
    setMappingEditActive(true);
    await loadConfiguration();
  };

  const deleteExternalSystem = async () => {
    const system = sets.find((item) => item.id === editingSystemId);
    if (!system) return;
    const mappingCount = allMappings.filter((item) => item.identifier_set_id === editingSystemId).length;
    const ok = await confirm(
      `This permanently deletes External System "${system.main_id}"${
        mappingCount ? ` and its ${mappingCount} active mapping${mappingCount === 1 ? "" : "s"}` : ""
      }. This action cannot be undone.`,
      { title: "Delete External System", okLabel: "Delete", cancelLabel: "Cancel" }
    );
    if (!ok) return;

    setSaving(true);
    const { error } = await supabase.from("external_identifier_sets").delete().eq("id", editingSystemId);
    setSaving(false);
    if (error) {
      await logAudit({ category: "integration", action: "external_identifier_system_delete_failed", success: false,
        detail: { identifier_set_id: editingSystemId, error: error.message } });
      await alert(`Unable to delete External System: ${error.message}`, { title: "External IDs" });
      return;
    }
    await logAudit({ category: "integration", action: "external_identifier_system_deleted", success: true,
      detail: { identifier_set_id: editingSystemId, main_id: system.main_id, mapping_count: mappingCount } });
    setEditingSystemId("");
    setMappingEditActive(false);
    setSelectedSystemId("");
    setModuleCode("");
    setFunctionId("");
    setMainId("");
    setRows([blankRow()]);
    await loadConfiguration();
  };

  const saveMappings = async () => {
    if (!selectedFunction || !mainId.trim() || !orgId) {
      await alert("Select a module, function, Main ID, and organization.", { title: "External IDs" });
      return;
    }
    const context = { mainId, functionCode: selectedFunction.function_code };
    const mappingRows = rows.map((row) => ({
      ...row,
      identifier_value: row.external_id,
    }));
    const validationError = validateFundMappingRows(mappingRows, context);
    if (validationError) {
      await alert(validationError, { title: "External IDs" });
      return;
    }
    const payloadRows = rows
      .filter((row) => row.fp_investment_fund_id && row.external_id.trim())
      .map((row) => ({
        fp_investment_fund_id: row.fp_investment_fund_id,
        identifier_value: row.external_id.trim(),
      }));

    setSaving(true);
    let identifierSet = selectedSet;
    if (!identifierSet) {
      const { data, error } = await supabase.from("external_identifier_sets").insert({
        function_id: selectedFunction.id,
        main_id: mainId.trim(),
        normalized_main_id: normalizeMainId(mainId),
      }).select("*").single();
      if (error) {
        setSaving(false);
        await alert(`Unable to create Main ID: ${error.message}`, { title: "External IDs" });
        return;
      }
      identifierSet = data;
    }

    const { data: savedRows, error } = await supabase.rpc("external_identifiers_replace_fund_mappings", {
      p_identifier_set_id: identifierSet.id,
      p_org_id: orgId,
      p_rows: payloadRows,
    });
    setSaving(false);
    if (error) {
      await logAudit({ category: "integration", action: "external_id_mappings_save_failed", success: false,
        detail: { identifier_set_id: identifierSet.id, org_id: orgId, error: error.message } });
      await alert(`Unable to save mappings: ${error.message}`, { title: "External IDs" });
      return;
    }
    await logAudit({ category: "integration", action: "external_id_mappings_saved", success: true,
      detail: { identifier_set_id: identifierSet.id, org_id: orgId, row_count: payloadRows.length } });
    await loadConfiguration();
    setSelectedSystemId(identifierSet.id);
    setShowNewContext(false);
    setNewContextReady(false);
    setEditingSystemId("");
    setMappingEditActive(false);
    setRows([
      ...(savedRows || []).map((row) => ({
        ...row,
        rowKey: row.id,
        external_id: row.identifier_value,
        __isEditing: false,
      })),
      blankRow(),
    ]);
  };

  if (profileLoading || loading) return <div className="p-5 text-muted">Loading External IDs…</div>;
  if (!isPlatformAdmin) return <div className="p-5 text-red-600">Platform administrator access required.</div>;

  return (
    <div className="space-y-6">
      <SectionCard title="Organization">
        <DropdownSelect label="Organization" value={orgId} onChange={selectOrganization}
          options={orgs.map((org) => ({ value: org.id, label: org.name }))}
          placeholder="Select organization…" className="w-full md:w-96" />
      </SectionCard>

      {orgId ? (
        <SectionCard title="External Systems">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted">
              Select an External System to view its mappings or create another system.
            </p>
            <button type="button" onClick={startNewExternalSystem}
              className="btn btn-primary">New</button>
          </div>
          {organizationSystems.length ? (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/20 text-left text-muted"><tr>
                  <th className="px-3 py-2 font-medium">Module</th>
                  <th className="px-3 py-2 font-medium">Function</th>
                  <th className="px-3 py-2 font-medium">External System</th>
                  <th className="px-3 py-2 font-medium">Mappings</th>
                  <th className="px-3 py-2"><span className="sr-only">Action</span></th>
                </tr></thead>
                <tbody>{organizationSystems.map((system) => (
                  <tr key={system.id} onClick={() => openExternalSystem(system)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") openExternalSystem(system);
                    }}
                    role="button" tabIndex={0}
                    className={`cursor-pointer border-t border-border ${
                      selectedSystemId === system.id
                        ? "bg-blue-50 dark:bg-blue-900/30"
                        : "hover:bg-muted/20"
                    }`}>
                    <td className="px-3 py-2">{system.function?.module_label || "—"}</td>
                    <td className="px-3 py-2">{system.function?.function_label || "—"}</td>
                    <td className="px-3 py-2">{system.main_id}</td>
                    <td className="px-3 py-2">{system.mappingCount}</td>
                    <td className="px-3 py-2 text-right"><button type="button"
                      onClick={(event) => { event.stopPropagation(); editExternalSystem(system); }}
                      className="text-app hover:text-brand" title="Edit External System"
                      aria-label={`Edit ${system.main_id}`}><Pencil size={16} /></button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <div className="text-sm text-muted">No External Systems have been mapped for this organization.</div>}

          {showNewContext || editingSystemId ? (
            <div className="mt-5 border-t border-border pt-5">
              <h3 className="mb-3 font-medium text-app">
                {editingSystemId ? "Edit External System" : "New External System"}
              </h3>
              <div className="grid gap-4 md:grid-cols-3">
                <DropdownSelect label="Module" value={draftModuleCode}
                  onChange={(value) => {
                    setDraftModuleCode(value); setDraftFunctionId(""); setDraftMainId(""); setNewContextReady(false);
                  }} options={MODULES} placeholder="Select module…" className="w-full" />
                <DropdownSelect label="Function" value={draftFunctionId}
                  onChange={(value) => { setDraftFunctionId(value); setNewContextReady(false); }}
                  options={draftFunctionOptions.map((item) => ({ value: item.id, label: item.function_label }))}
                  placeholder={draftModuleCode && !draftFunctionOptions.length ? "No functions configured" : "Select function…"}
                  disabled={!draftModuleCode || !draftFunctionOptions.length} className="w-full" />
                <div>
                  <TextInput label="External System / Main ID" value={draftMainId}
                    onChange={(value) => { setDraftMainId(value); setNewContextReady(false); }}
                    placeholder="Enter External System" disabled={!draftFunctionId}
                    className="w-full" list="external-main-id-options" />
                  <datalist id="external-main-id-options">
                    {sets.filter((item) => item.function_id === draftFunctionId)
                      .map((item) => <option key={item.id} value={item.main_id} />)}
                  </datalist>
                </div>
              </div>
              {draftModuleCode && !draftFunctionOptions.length ? (
                <p className="mt-3 text-sm text-muted">No External ID functions have been configured for this module yet.</p>
              ) : null}
              <div className="mt-4 flex items-center justify-between gap-3">
                <div>
                  {editingSystemId ? (
                    <button type="button" onClick={deleteExternalSystem} disabled={saving}
                      className="btn btn-ghost text-red-600 hover:text-red-700 disabled:opacity-60">
                      Delete
                    </button>
                  ) : null}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => {
                    setShowNewContext(false); setNewContextReady(false); setEditingSystemId("");
                    setMappingEditActive(false);
                  }} className="btn btn-ghost">Cancel</button>
                  <button type="button" onClick={editingSystemId ? saveExternalSystem : createExternalSystemContext}
                    disabled={saving} className="btn btn-primary">
                    {editingSystemId ? (saving ? "Saving…" : "Save External System") : "Create External System"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </SectionCard>
      ) : null}

      {showMappingGrid ? (
        <SectionCard title={`${selectedFunction.function_label}: ${mainId.trim()}`}>
          <p className="mb-4 text-sm text-muted">
            Map each External ID to an existing FundPlanner fund. Removing a saved row deactivates its mapping while retaining history.
          </p>
          {loadingMappings ? <div className="text-sm text-muted">Loading mappings…</div> : (
            <ExcelGrid columns={gridColumns} rows={rows} onChange={setRows}
              requestEditCell={requestEditCell} showCopyButton={true}
              allowPasteAppendRows={gridIsEditable}
              makePasteRow={() => blankRow()} />
          )}
          {gridIsEditable ? (
            <div className="mt-4 flex gap-3">
              <button type="button" onClick={saveMappings} disabled={saving || loadingMappings}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60">
                {saving ? "Saving…" : "Save mappings"}
              </button>
            </div>
          ) : null}
        </SectionCard>
      ) : null}
    </div>
  );
}
